import React from 'react';
import type {
  TaskAssignmentSimpleResponse} from 'src/api/ProactApiClient';
import {
  FeatureFlagEnum,
  TaskAssignmentStateEnum,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {CreateButton} from 'src/design/molecules/CreateButton';
import {FeatureFlagGuard} from 'src/app/auth/FeatureFlagGuard';
import {CheckboxList} from 'src/design/atoms/CheckboxList';
import {isPendingSync} from 'src/offline/pendingSync';
import {TasksPendingSyncBanner} from 'src/app/tasks/offline/TasksPendingSyncBanner';
import {
  TaskAssignmentRowPills,
  TasksDataListMeta,
  taskDocumentTitle,
  taskRowSubText,
} from 'src/app/tasks/taskHelpers';
import {DataHeader} from 'src/design/organisms/DataHeader';
import {DataList} from 'src/design/organisms/DataList';
import {useListState} from 'src/design/organisms/useListState';
import {useAppSelector} from 'src/redux/hooks';
import {useGetTaskAssignmentsListQuery} from 'src/redux/queries/taskAssignment';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useDebounce from 'src/shared/hooks/useDebounce';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const maxPageRows = 10;

export interface ListCurrentUserTasksProps {
  taskType: TaskTypeEnum;
}

export const ListCurrentUserTasks: React.FC<ListCurrentUserTasksProps> = ({
  taskType,
}) => {
  const {session} = useAppSelector(state => state.session);
  const userId = session?.user?.id;

  useSetDocumentTitle(taskDocumentTitle);
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();
  const {
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    currentPage,
    handlePageChange,
    start,
    length,
  } = useListState({
    identifier: `current-user-tasks-${taskType}`,
    pageSize: maxPageRows,
    defaultFilters: {
      assignmentStates: [
        String(TaskAssignmentStateEnum.Assigned),
        String(TaskAssignmentStateEnum.Viewed),
      ],
      assignedToMe: true,
    },
  });
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const statuses = (function () {
    if (taskType === TaskTypeEnum.Approval)
      return [
        {value: String(TaskAssignmentStateEnum.Assigned), label: 'Assigned'},
        {value: String(TaskAssignmentStateEnum.Completed), label: 'Completed'},
        {value: String(TaskAssignmentStateEnum.Rejected), label: 'Rejected'},
      ];
    else {
      return [
        {value: String(TaskAssignmentStateEnum.Assigned), label: 'Assigned'},
        {value: String(TaskAssignmentStateEnum.Viewed), label: 'Viewed'},
        {value: String(TaskAssignmentStateEnum.Completed), label: 'Completed'},
      ];
    }
  })();

  const {data, isLoading, error} = useGetTaskAssignmentsListQuery(
    {
      taskType: taskType,
      assignedToUserId: filters.assignedToMe ? userId! : undefined,
      assignmentStates: filters.assignmentStates.map((x: string) => +x),
      start,
      length,
      search: debouncedSearchTerm,
    },
    {skip: userId === undefined}
  );

  if (error) {
    setAlert(parseErrorMessage(error), 'error');
  }

  const indexListData: TaskAssignmentSimpleResponse[] = data?.data ?? [];
  const totalRecords = data?.totalRecords ?? 0;

  const handleNavigateToTask = (id: string | number) =>
    navigate(PathUtility.ViewTaskAssignment(taskType, id));

  const handleSearchTerm = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const {value} = e.target;
    setSearchTerm(value);
  };

  const handleStatusFilter = (statusValues: string[]) => {
    setFilters(prevState => ({
      ...prevState,
      ...{assignmentStates: statusValues},
    }));
  };

  const handleAddUserActionTask = (): void => {
    navigate(PathUtility.ActionCreate());
  };

  return (
    <>
      <TasksPendingSyncBanner />
      <DataHeader
        titleText={''}
        searchTerm={searchTerm}
        actions={
          taskType === TaskTypeEnum.Actions
            ? [
                <FeatureFlagGuard requiredFeatureFlag={FeatureFlagEnum.Actions}>
                  <CreateButton onClick={handleAddUserActionTask}>
                    Add Action
                  </CreateButton>
                </FeatureFlagGuard>,
              ]
            : []
        }
        filters={[
          <CheckboxList
            label="Statuses"
            values={statuses}
            selected={filters.assignmentStates}
            onChange={handleStatusFilter}
          />,
        ]}
        onSearchTermChange={handleSearchTerm}
        isFilterActive={false}
      />
      <DataList
        isLoading={isLoading}
        data={indexListData}
        meta={TasksDataListMeta}
        currentPage={currentPage}
        totalRecords={totalRecords}
        recordsPerPage={maxPageRows}
        onRowClick={handleNavigateToTask}
        onIsRowDisabled={isPendingSync}
        onPageChange={handlePageChange}
        onGetPillMapping={(item: TaskAssignmentSimpleResponse) =>
          TaskAssignmentRowPills(item)
        }
        onRowTextRender={item => item.taskDefinition?.subject}
        onRowSubTextRender={taskRowSubText}
        hasSearchTerm={searchTerm.trim().length > 0}
        hasCreationLogic={false}
      />
    </>
  );
};
